import React, { useEffect, useRef, useState } from 'react';
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import "../component css/Hero.css";
import V_image_sm from "../assets/images/homepage/V_image.png?w=1200&format=webp";
import V_image_lg from "../assets/images/homepage/V_image.png?w=2400&format=webp";
import bottom_pattern from "../assets/images/homepage/bottom_pattern.svg";
import profile from "../assets/images/about/profile.png?w=900&format=webp";
import projectsCardImg from "../assets/images/homepage/projects_card.png?w=800&format=webp";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const TITLES = [
  { role: "FRONT-END DEVELOPER",  desc: "Building modern web experiences..." },
  { role: "FULL-STACK DEVELOPER", desc: "Creating complete end-to-end solutions..." },
];

const Hero = () => {
  const navigate = useNavigate();
  const vImageRef = useRef(null);
  const [titleIndex, setTitleIndex] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => {
      setTitleIndex((prev) => (prev + 1) % TITLES.length);
    }, 2000);
    return () => clearTimeout(id);
  }, [titleIndex]);
  const heroHeadingRef = useRef(null);
  const heroVabzRef = useRef(null);
  const heroSubtextRef = useRef(null);
  const vImageWrapRef = useRef(null);
  const cardsWrapRef = useRef(null);
  const projectsCardRef = useRef(null);
  const aboutCardRef = useRef(null);
  const heroSectionRef = useRef(null);
  const scrollIndicatorRef = useRef(null);

  // Single-click navigation: tear down the pinned ScrollTrigger,
  // scroll to top, then route. Fixes the "have to click twice" issue.
  const goTo = (e, path) => {
    e.preventDefault();
    ScrollTrigger.getAll().forEach((t) => t.kill());
    document.body.classList.remove("hero-pinned");
    window.scrollTo(0, 0);
    navigate(path);
  };

  // ── Scroll transition: buttons expand into cards ─────────────────────────
  useEffect(() => {
    const projectsCard = projectsCardRef.current;
    const aboutCard = aboutCardRef.current;
    if (!projectsCard || !aboutCard) return;

    const isMobile = window.innerWidth <= 600;

    // ── Hardcoded sizes — no DOM measuring, so this always works ─────────
    const BTN_W = 170;
    const BTN_H = 58;
    const CARD_W = isMobile ? 220 : 290;
    const CARD_H = isMobile ? 270 : 390;
    // Use visualViewport height on iOS so GSAP matches the svh CSS value
    const vh = (window.visualViewport?.height ?? window.innerHeight);
    const xSpread = isMobile ? 0 : 180;
    const yDown   = vh * (isMobile ? 0.27 : 0.36);

    // Button (start) state
    gsap.set(projectsCard, {
      opacity: 1, x: -xSpread, width: BTN_W, height: BTN_H,
      borderRadius: 8, rotateX: 0, rotateY: 0, scale: 1, yPercent: 0,
      transformPerspective: 1100, transformOrigin: "center center",
    });
    gsap.set(aboutCard, {
      opacity: 1, x: xSpread, width: BTN_W, height: BTN_H,
      borderRadius: 8, rotateX: 0, rotateY: 0, scale: 1, yPercent: 0,
      transformPerspective: 1100, transformOrigin: "center center",
    });
    gsap.set(cardsWrapRef.current, { xPercent: -50, yPercent: -50, y: yDown });

    const allContent = [
      ...projectsCard.querySelectorAll(".hero_card__image-wrap, .hero_card__overlay, .hero_card__info"),
      ...aboutCard.querySelectorAll(".hero_card__image-wrap, .hero_card__overlay, .hero_card__info"),
    ];
    const allLabels = [
      projectsCard.querySelector(".hero_card__btn-label"),
      aboutCard.querySelector(".hero_card__btn-label"),
    ].filter(Boolean);

    gsap.set(allContent, { opacity: 0 });
    if (allLabels.length) gsap.set(allLabels, { opacity: 1 });

    // ── Scroll-pin animation (all breakpoints) ────────────────────────────
    document.body.classList.add("hero-pinned");

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: heroSectionRef.current,
          start: "top top",
          end: "+=900",
          scrub: 1.2,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          onEnter: () => document.body.classList.add("hero-pinned"),
          onLeave: () => document.body.classList.remove("hero-pinned"),
          onEnterBack: () => document.body.classList.add("hero-pinned"),
          onLeaveBack: () => document.body.classList.remove("hero-pinned"),
          onUpdate: (self) => {
            cardsWrapRef.current?.classList.toggle("is-active", self.progress > 0.85);
          },
        },
      });

      // 1. Name + roles fade up and out
      tl.to(
        [heroHeadingRef.current, heroVabzRef.current, heroSubtextRef.current],
        { opacity: 0, yPercent: -40, stagger: 0.03, duration: 0.25, ease: "power2.inOut" },
        0
      );

      // 2. V image lifts up and fades
      tl.to(vImageWrapRef.current, {
        yPercent: -35, opacity: 0, duration: 0.25, ease: "power2.inOut",
      }, 0.05);

      // 2b. Scroll-down hint fades out early like the rest of the intro copy,
      // then fades back in once the cards have fully formed, relocated to
      // the bottom of the hero section instead of under the role text.
      // The reposition is a `tl.set()` on this SAME scrubbed timeline (not a
      // separate onUpdate/classList toggle) — onUpdate's self.progress is
      // immediate, while scrub:1.2 makes the timeline's own playback lag
      // behind it, so a position swap driven by onUpdate could land before
      // or after the opacity tween and flash at the wrong spot mid-scroll.
      // Opacity-only for the tween itself: this element's centering comes
      // from a CSS transform (translateX(-50%)), so animating GSAP's x/y
      // here instead would clobber it.
      tl.to(scrollIndicatorRef.current, { opacity: 0, duration: 0.2, ease: "power2.inOut" }, 0);
      tl.set(scrollIndicatorRef.current, { top: "auto", bottom: "5%" }, 0.85);
      tl.to(scrollIndicatorRef.current, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0.85);

      // 3. Wrapper rises to centre
      tl.to(cardsWrapRef.current, { y: 0, xPercent: -50, yPercent: -50, duration: 0.7, ease: "power3.out" }, 0.30);

      // 4. Buttons expand into full cards
      tl.to([projectsCard, aboutCard], {
        x: 0, width: CARD_W, height: CARD_H, borderRadius: 20,
        duration: 0.7, ease: "power3.out",
      }, 0.30);

      // 5. Labels fade out as cards grow
      if (allLabels.length) {
        tl.to(allLabels, { opacity: 0, duration: 0.2, ease: "power2.in" }, 0.30);
      }

      // 6. Card content fades in once expanded
      tl.to(allContent, { opacity: 1, stagger: 0.06, duration: 0.4, ease: "power2.out" }, 0.52);

    }, heroSectionRef);

    return () => {
      ctx.revert();
      document.body.classList.remove("hero-pinned");
    };
  }, []);

  return (
    <>
      <section id="hero" ref={heroSectionRef}>
        <div className="hero_section">
          <div className="pattern">
            <img src="/top_pattern.svg" alt="Square Pattern" className="top_pattern_img" fetchpriority="high" width="1919" height="390" />
            <img src={bottom_pattern} alt="Square Pattern" className="bottom_pattern_img" width="1920" height="389" />
          </div>

          <div className="hero_content">
            {/* V shape */}
            <div className="V_image" ref={vImageWrapRef}>
              <img ref={vImageRef} src={V_image_lg} srcSet={`${V_image_sm} 900w, ${V_image_lg} 1536w`} sizes="(max-width: 768px) 100vw, 1536px" alt="V image" className="V_image_img" fetchpriority="high" width="1536" height="1024" />
              <span className="silver-star star1"></span>
              <span className="silver-star star2"></span>
              <span className="silver-star star3"></span>
              <span className="silver-star star4"></span>
            </div>

            {/* Name */}
            <h1 className="hero_heading" ref={heroHeadingRef}>VAIBHAV KAPOOR</h1>
            <h2 className="vabz_subtext" ref={heroVabzRef}>VABZ</h2>
            <div className="hero_subtext" ref={heroSubtextRef}>
              {TITLES.map(({ role, desc }, index) => (
                <motion.span
                  key={role}
                  className="hero_subtext__title"
                  initial={{ opacity: 0, y: 60 }}
                  transition={{ type: "spring", stiffness: 50 }}
                  animate={
                    titleIndex === index
                      ? { y: 0, opacity: 1 }
                      : { y: titleIndex > index ? -60 : 60, opacity: 0 }
                  }
                >
                  {role}
                  <span className="hero_subtext__desc">{desc}</span>
                </motion.span>
              ))}
            </div>

            {/* Buttons → Cards */}
            <div className="hero_cards" ref={cardsWrapRef}>
              <Link
                to="/projects"
                className="hero_card hero_card--projects"
                ref={projectsCardRef}
                onClick={(e) => goTo(e, "/projects")}
              >
                <div className="hero_card__image-wrap">
                  <img src={projectsCardImg} alt="Projects preview" width="340" height="409" />
                </div>
                <div className="hero_card__overlay"></div>
                <div className="hero_card__info">
                  <div className="hero_card__meta">
                    <span className="hero_card__category">Projects</span>
                    <span className="hero_card__year">0005</span>
                  </div>
                  <h3>Project Showcase</h3>
                  <p>Selected work, creative projects, and case studies in design, development, and branding.</p>
                </div>
                <span className="hero_card__btn-label">Projects</span>
              </Link>

              <Link
                to="/about"
                className="hero_card hero_card--about"
                ref={aboutCardRef}
                onClick={(e) => goTo(e, "/about")}
              >
                <div className="hero_card__image-wrap">
                  <img src={profile} alt="Vaibhav Kapoor" width="400" height="300" />
                </div>
                <div className="hero_card__overlay"></div>
                <div className="hero_card__info">
                  <div className="hero_card__meta">
                    <span className="hero_card__category">Vabz</span>
                    <span className="hero_card__year">2026</span>
                  </div>
                  <h3>Vaibhav Kapoor</h3>
                  <p>Creative web developer, designer, and entrepreneur passionate about technology and innovation.</p>
                </div>
                <span className="hero_card__btn-label">About Me</span>
              </Link>
            </div>
          </div>

          <div className="scroll-indicator" ref={scrollIndicatorRef}>
            <div className="scroll-indicator-content">
              <span className="scroll-indicator-text">Scroll Down</span>
              <div className="scroll-indicator-arrow">
                <span className="arrow"></span>
                <span className="arrow"></span>
                <span className="arrow"></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;